import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Input as RInput,
  InputGroup,
  FormGroup,
  InputGroupText,
  Label,
  Button,
  CustomInput,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  InputGroupAddon,
} from "reactstrap";
import { Link } from "react-router-dom";
import cx from "classnames";

import { Icon } from "./icon";
import Select2 from "./Select2";
import MultiSelect from "./multiSelect";
import TagsInput from "./tagsInput";
import ContentEditable from "./ContentEditable";
import Multiline from "./multiline";
import MultiSelectPage from "./multiSelectPage";

import global from "../../scss/dhp.scss";
import LoadFlagImage from "./LoadFlagImage";
import EmailRecipient from "./emailRecipient";
import ColorPalette from "./ColorPalette";
import LookupField from "./LookupField";
import Calendar from "./Calendar";
import TabularMultiSelect from "./tabularMultiSelect";
import ReactstrapDropdown from "./reactstrapDropdown";
import DateTimeField from "./DateTimeField";
import CountdownCalender from "./CountdownCalender";

let style = Object.assign({}, global);

export const Input = ({
  returnType,
  addonType,
  groupId,
  type,
  toggleHandler,
  checkBoxClass,
  updateState,
  spinnerClass,
  onIncrease,
  onDecrease,
  extn,
  isdOptions,
  fields,
  phoneDefaultValue,
  radioClass,
  customFileClass,
  maxLength,
  minLength,
  inputGroupClass,
  multilineClass,
  decreasedisable,
  increasedisable,
  formGroupClass,
  leftAddonEnabled,
  leftAddonData,
  rightAddonEnabled,
  rightAddonData,
  isEnable,
  discountOptions,
  changeDiscountType,
  defaultDiscountType,
  showAddonButton = true,
  dropdownContainer,
  customDropdownMenuClass,
  closeOnScroll = false,
  onOpenRecordSelector,
  hasOutsideExternalData = null,
  ...props
}) => {
  let inputElement;
  let inputGroupAddon;
  let inputComponent;
  let inputType = ["search"].includes(type)
    ? "text"
    : ["password_length"].includes(type)
    ? "password"
    : ["numeric_percentage", "group_addon_field", "discount"].includes(type)
    ? "number"
    : type;

  let rInput = (
    <RInput
      autoComplete="off"
      type={inputType}
      maxLength={maxLength ?? 255}
      minLength={minLength ?? 1}
      cssModule={style}
      style={{
        ...props.style,
        ...(type === "left_addOn_field" && {
          cursor: hasOutsideExternalData ? "default" : "pointer",
        }),
      }}
      {...props}
    />
  );

  const dropdownRef = useRef(null);

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen(prevState => !prevState);

  const handleTooltipEnter = e => {
    const input = e.currentTarget.querySelector("input");

    if (!input) return;

    const isOverflowing = Math.ceil(input.scrollWidth) > Math.ceil(input.clientWidth);

    setIsTooltipOpen(isOverflowing);
  };

  const handleTooltipLeave = () => {
    setIsTooltipOpen(false);
  };

  const getTooltipInput = (input, targetId) => {
    if (!props.showTooltipOnOverflow) return input;

    return React.cloneElement(input, {
      id: targetId,

      onMouseEnter: e => {
        input.props.onMouseEnter?.(e);

        const el = e.currentTarget;

        if (!el) return;

        setIsTooltipOpen(Math.ceil(el.scrollWidth) > Math.ceil(el.clientWidth));
      },

      onMouseLeave: e => {
        input.props.onMouseLeave?.(e);
        setIsTooltipOpen(false);
      },
    });
  };

  const renderOverflowTooltip = target => {
    if (!props.showTooltipOnOverflow) return null;

    return (
      <Tooltip
        placement="bottom"
        target={target}
        isOpen={isTooltipOpen}
        toggle={handleTooltipLeave}
        fade={false}
        className="crm-record-tooltip">
        {props.value}
      </Tooltip>
    );
  };

  useEffect(() => {
    if (closeOnScroll) {
      const hideDiscountDropdownOnScroll = e => {
        if (e.target.classList.contains("select__menu-list")) return;
        if (dropdownOpen) toggle();
      };
      document.addEventListener("scroll", hideDiscountDropdownOnScroll, true);
      return () => {
        document.removeEventListener("scroll", hideDiscountDropdownOnScroll, true);
      };
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen) {
    }
    setTimeout(() => {
      const currentDropdown = document.querySelectorAll(".input-field-discount-area.show")[0];
      if (currentDropdown) {
        // Check if menu has placed top of the dropdown and container present
        if (dropdownRef?.current && dropdownRef?.current?.menuRef?.current && dropdownContainer) {
          const parentDropdown = dropdownRef.current.containerRef.current;
          const menuElement = dropdownRef.current.menuRef.current;

          const parentDropdownStyle = parentDropdown.getBoundingClientRect();
          const menuElementStyle = menuElement.getBoundingClientRect();
          if (menuElementStyle?.top < parentDropdownStyle?.top) {
            let calculatedTop = parentDropdownStyle.top + parentDropdownStyle.height + 2;

            menuElement.style.transform = `translate3d(${menuElementStyle.left}px, ${calculatedTop}px, 0px)`;
            menuElement.style.opacity = 1;
          }
          menuElement.style.opacity = 1;
        }
      }
    }, 15);
  }, [dropdownOpen]);

  if (!returnType || returnType === "formGroup") {
    switch (type) {
      case "text":
        {
          const targetId = `tooltip-${props.id || props.name}`;
          inputElement = (
            <InputGroup
              cssModule={style}
              className={cx(
                { [style["icon-right"]]: !props?.hideIconRight },
                { [style["disable-field"]]: props.readOnly }
              )}
              id={targetId}
              onMouseEnter={handleTooltipEnter}
              onMouseLeave={handleTooltipLeave}>
              {rInput}
              {props.readOnly && (
                <div className={cx(style["input-group-append"])}>
                  <InputGroupText className={cx(style["input-group-addon"])}>
                    <Icon icon="ui-lock2" />
                  </InputGroupText>
                </div>
              )}
              {renderOverflowTooltip(targetId)}
            </InputGroup>
          );
        }
        break;

      case "lookup":
        inputElement = <LookupField {...props} />;
        break;

      case "url":
        inputElement = (
          <InputGroup
            cssModule={style}
            className={cx(style["icon-right"], { [style["disable-field"]]: props.readOnly })}>
            {rInput}
            {props.readOnly && (
              <div className={cx(style["input-group-append"])}>
                <InputGroupText className={cx(style["input-group-addon"])}>
                  <Icon icon="ui-lock2" />
                </InputGroupText>
              </div>
            )}
          </InputGroup>
        );
        break;

      case "website":
        inputElement = (
          <>
            <InputGroup
              cssModule={style}
              className={cx(style["icon-right"], { [style["disable-field"]]: props.readOnly })}>
              {rInput}
              {props.readOnly && (
                <div className={cx(style["input-group-append"])}>
                  <InputGroupText className={cx(style["input-group-addon"])}>
                    <Icon icon="ui-lock2" />
                  </InputGroupText>
                </div>
              )}
            </InputGroup>
          </>
        );

      case "email":
        inputElement = (
          <InputGroup
            className={cx(style["icon-right"], {
              [style["disable-field"]]: props.readOnly,
            })}>
            {rInput}
            {props.readOnly && (
              <div className="input-group-addon">
                <Icon icon="ui-lock2" />
              </div>
            )}
          </InputGroup>
        );
        break;

      case "password":
      case "text_password":
        inputElement = (
          <InputGroup cssModule={style} className={style["icon-right"]}>
            {rInput}
            <div className={cx(style["input-group-append"])}>
              <InputGroupText className={cx(style["input-group-addon"], style["togglepassword"])}>
                <Link to="#" onClick={e => e.preventDefault()}>
                  <Icon
                    icon={type === "text_password" ? "eye-cross" : "eye2"}
                    onClick={() => toggleHandler(props.name)}
                  />
                </Link>
              </InputGroupText>
            </div>
          </InputGroup>
        );
        break;

      case "phone":
        inputElement = (
          <div className={cx(style["input-group"], style["icon-left"], style["field-with-dropdown"])}>
            <div className={cx(style["input-group-append"], style["input-group-addon"])}>
              <Select2
                classNamePrefix="select"
                customLabel={true}
                labelField="isd_code"
                defaultValue={phoneDefaultValue}
                getOptionLabel={option => (
                  <span className={style["option-label"]}>
                    <LoadFlagImage isoCode={option.iso_code} />
                    {option.name} <span className={cx(style["isd-code"])}> +{option.isd_code}</span>
                  </span>
                )}
                subField="iso_code"
                updateState={updateState}
                getOptionValue={option => [option.name, option.isd_code]}
                options={isdOptions}
                cssModule={style}
                {...props}
              />
            </div>

            <RInput
              type="text"
              pattern="[0-9]*"
              value={fields["pnumber"]}
              onChange={e => updateState(e, props.name, "pnumber")}
              className={style["px-1"]}
              cssModule={style}
              {...props}
            />
            {extn && (
              <div className={cx(style["input-group-prepend"], style["input-group-addon"], style["w-25"])}>
                <RInput
                  type="number"
                  placeholder="Extn"
                  value={fields["extn"]}
                  onChange={e => updateState(e, props.name, "extn")}
                  {...props}
                />
              </div>
            )}
          </div>
        );
        break;

      case "checkbox":
        inputElement = (
          <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
            {rInput}
            <Label className={cx(style["form-check-label"], style["form-check-label"])} htmlFor={props.id}>
              {props.text}
            </Label>
          </div>
        );
        break;

      case "radio":
        inputElement = (
          <div
            className={cx(
              style["custom-control"],
              style["custom-radio"],
              style["custom-control-inline"],
              style["form-check"]
            )}>
            {rInput}
            <Label className={cx(style["form-check-label"], style["form-check-label"])} htmlFor={props.id}>
              {props.text}
            </Label>
          </div>
        );
        break;

      case "dropdown":
        inputElement = (
          <Select2
            updateState={updateState}
            customDropdownMenuClass={customDropdownMenuClass}
            cssModule={style}
            closeOnScroll={closeOnScroll}
            {...props}
          />
        );
        break;

      case "reactstrap-dropdown":
        inputElement = <ReactstrapDropdown updateState={updateState} cssModule={style} {...props} />;
        break;

      case "colorpalette":
        inputElement = <ColorPalette {...props} />;
        break;

      case "password_length":
        inputElement = rInput;
        break;

      case "spinner":
        {
          inputElement = (
            <div className="input-area">
              <InputGroup className={inputGroupClass}>
                {showAddonButton && (
                  <div className={cx(style["input-group-prepend"])}>
                    <Button
                      type="button"
                      outline
                      color="secondary"
                      className="btn-minus btn-decrement"
                      disabled={decreasedisable ?? false}>
                      {decreasedisable && <span className="disable">-</span>}
                      {!decreasedisable && <strong onClick={() => onDecrease(props.id)}>-</strong>}
                    </Button>
                  </div>
                )}
                <RInput autoComplete="off" type="number" cssModule={style} {...props} />{" "}
                {showAddonButton && (
                  <div className={cx(style["input-group-append"])}>
                    <Button
                      type="button"
                      outline
                      color="secondary"
                      className="btn-plus btn-increment"
                      disabled={increasedisable ?? false}>
                      {increasedisable && <span className="disable">+</span>}
                      {!increasedisable && <strong onClick={() => onIncrease(props.id)}>+</strong>}
                    </Button>
                  </div>
                )}
              </InputGroup>
            </div>
          );
        }
        break;

      case "tag":
        inputElement = <TagsInput updateState={updateState} cssModule={style} {...props} />;
        break;

      case "file":
        const { id, name, filename = "Choose File", customFileClass, onChange, ...rest } = props;

        const handleChange = e => {
          const input = e.target;
          const label = input.nextElementSibling;

          const fileName = input.files?.[0]?.name;

          if (label) {
            label.textContent = fileName || filename;
          }

          if (onChange) onChange(e);
        };

        inputElement = (
          <>
            <Label for={id}>{name}</Label>

            <div className={cx(style["custom-file"], style[customFileClass])}>
              <input type="file" id={id} className="custom-file-input" onChange={handleChange} {...rest} />

              <Label className="custom-file-label" htmlFor={id}>
                {filename}
              </Label>
            </div>
          </>
        );
        break;

      case "multi_select":
        inputElement = (
          <MultiSelect
            updateState={updateState}
            cssModule={style}
            dropdownContainer={dropdownContainer}
            customDropdownMenuClass={customDropdownMenuClass}
            closeOnScroll={closeOnScroll}
            {...props}
          />
        );
        break;

      case "multiline":
        const property = maxLength ? { ...props, maxLength } : props;
        inputElement = <Multiline className={style["form-control"]} {...property} />;
        break;

      case "multi_select_page":
        inputElement = <MultiSelectPage updateState={updateState} cssModule={style} {...props} />;
        break;

      case "tabular_multi_select":
        inputElement = <TabularMultiSelect updateState={updateState} cssModule={style} {...props} />;
        break;

      case "social":
        inputElement = (
          <InputGroup
            className={cx(style["icon-right"], {
              [style["disable-field"]]: props.readOnly,
            })}>
            {rInput}
            {props.readOnly && (
              <div className="input-group-addon">
                <img src={props.addonicon} alt="social Icon" className={style["pe-3"]} />
              </div>
            )}
          </InputGroup>
        );
        break;

      case "email_recipient":
        inputElement = <EmailRecipient updateState={updateState} cssModule={style} {...props} />;
        break;

      case "date_calendar":
        inputElement = <Calendar updateState={updateState} cssModule={style} {...props} />;
        break;

      case "countdown_calendar":
        inputElement = <CountdownCalender updateState={updateState} cssModule={style} {...props} />;
        break;

      case "numeric_percentage":
        inputElement = (
          <InputGroup cssModule={style} className={cx(style["icon-right"], style["add-border-bg"])}>
            {rInput}
            <div className={cx(style["input-group-addon"], style["input-group-append"])}>
              <span>%</span>
            </div>
          </InputGroup>
        );
        break;

      case "number":
        inputElement = (
          <RInput
            type="number"
            placeholder={props?.placeholder}
            value={props.value}
            onChange={e => updateState(e, props.name, "numberField")}
            cssModule={style}
            {...props}
          />
        );
        break;

      case "group_addon_field":
        const [showTooltip, setShowTooltip] = useState(false);
        // Only show tooltip if truncated
        const mouseEnter = e => {
          setShowTooltip(e.target.scrollWidth > e.target.clientWidth);
        };

        inputElement = (
          <InputGroup
            cssModule={style}
            className={cx(style["group-addon-field"], {
              [style["add-left"]]: leftAddonEnabled,
              [style["add-right"]]: rightAddonEnabled,
              [style["disable"]]: isEnable,
            })}>
            {leftAddonEnabled && (
              <div className={cx(style["input-group-prepend"])}>
                <InputGroupText className={style["text-muted"]}>{leftAddonData}</InputGroupText>
              </div>
            )}
            {rInput}
            {rightAddonEnabled && (
              <div className={cx(style["input-group-append"])}>
                <InputGroupText className={cx(style["text-muted"], style["ps-2"], style["custom-tooltip"])}>
                  {rightAddonData && "/"}{" "}
                  <span
                    className={style["ms-1"]}
                    onMouseEnter={e => mouseEnter(e)}
                    onMouseLeave={() => setShowTooltip(false)}>
                    {rightAddonData}
                  </span>
                  {showTooltip && (
                    <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                      {rightAddonData}
                    </div>
                  )}
                </InputGroupText>
              </div>
            )}
          </InputGroup>
        );
        break;

      case "discount":
        inputElement = (
          <div
            className={cx(style["input-group"], style["icon-left"], style["field-with-dropdown"], {
              [style["disable"]]: isEnable,
            })}>
            <div className={cx(style["input-group-addon"], style["input-group-append"])}>
              <Dropdown ref={dropdownRef} isOpen={dropdownOpen} toggle={toggle} disabled={isEnable ?? false}>
                <DropdownToggle tag="a" caret>
                  {defaultDiscountType ?? "%"}
                  <Icon icon="ui-arrow-down" additionalclass={"ms-1"} />
                </DropdownToggle>
                <DropdownMenu
                  className={cx(
                    style["symbol-drop"],
                    style["shadow"],
                    style["border-0"],
                    style["rounded"],
                    // style["ms-n3"],
                    "input-field-discount-area",
                    { [customDropdownMenuClass]: customDropdownMenuClass }
                  )}
                  container={dropdownContainer ? dropdownContainer : undefined}
                  style={dropdownContainer && { opacity: 0 }}>
                  <DropdownItem tag="a" onClick={() => changeDiscountType("%")}>
                    %
                  </DropdownItem>
                  {discountOptions?.map((item, itemIdx) => (
                    <DropdownItem key={itemIdx} tag="a" onClick={() => changeDiscountType(item)}>
                      {item}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>

            {rInput}
          </div>
        );
        break;

      case "date_time":
        inputElement = <DateTimeField updateState={updateState} maxLength={maxLength} {...props} />;
        // inputElement = (
        //   <div className={cx(style["d-flex"], style["dateTime-field"])}>
        //     <div className={cx(style["custom-date-picker"], style["w-65"])}>
        //       <Calendar updateState={updateState} cssModule={style} {...props} />
        //     </div>
        //     <div className={cx(style["d-flex"], style["time-section"], style["w-35"])}>
        //       <RInput
        //         autoComplete="off"
        //         type={"text"}
        //         maxLength={maxLength ?? 255}
        //         minLength={minLength ?? 1}
        //         cssModule={style}
        //         value={"10:40"}
        //       />
        //       {/* <span className="time-value">{"10:40"}</span> */}
        //       <UncontrolledDropdown>
        //         <DropdownToggle tag={"a"} caret>AM
        //           <Icon icon="ui-arrow-down" additionalclass={"ms-2"} />
        //         </DropdownToggle>
        //         <DropdownMenu right className={cx(style["shadow"], style["border-0"])}>
        //           <DropdownItem  tag={"a"}>AM</DropdownItem>
        //           <DropdownItem  tag={"a"}>PM</DropdownItem>
        //         </DropdownMenu>
        //       </UncontrolledDropdown>
        //     </div>
        //   </div>
        // );
        break;

      case "dynamic_lock_icon_text":
        inputElement = (
          <InputGroup
            cssModule={style}
            className={cx(style["icon-right"], { [style["disable-field"]]: props.readOnly })}>
            {rInput}
            {props.readOnly && (
              <div className={cx(style["input-group-addon"], style["input-group-append"])}>
                <Icon icon={props.dynamicIconName || "ui-lock2"} additionalclass="font-18" />
              </div>
            )}
          </InputGroup>
        );
        break;

      case "left_addOn_field":
        {
          const targetId = `tooltip-${props.id || props.name}`;
          const leftAddonInput = getTooltipInput(rInput, targetId);

          inputElement = (
            <>
              <InputGroup
                cssModule={style}
                className={cx(style["group-addon-field"], style["relation-absolute"], {
                  [style["add-left"]]: leftAddonEnabled,
                  [style["disable"]]: isEnable,
                })}>
                {leftAddonEnabled && (
                  <div className={cx(style["input-group-prepend"])}>
                    <InputGroupText>{leftAddonData}</InputGroupText>
                  </div>
                )}
                {leftAddonEnabled && (
                  <div
                    className={cx(style["input-group-append"], style["crm-search-append"])}
                    style={{
                      cursor: hasOutsideExternalData ? "default" : "pointer",
                    }}
                    onClick={() => {
                      if (hasOutsideExternalData) return;
                      onOpenRecordSelector();
                    }}>
                    <InputGroupText
                      style={{
                        cursor: hasOutsideExternalData ? "default" : "pointer",
                      }}
                      className={cx(style["h-100"])}>
                      {" "}
                      <Icon icon="ui-search" />
                    </InputGroupText>
                  </div>
                )}
                <div
                  id={targetId}
                  onMouseEnter={handleTooltipEnter}
                  onMouseLeave={handleTooltipLeave}
                  onClick={() => {
                    if (hasOutsideExternalData) return;
                    onOpenRecordSelector();
                  }}
                  style={{
                    cursor: "pointer",
                    flex: 1,
                  }}>
                  {leftAddonInput}
                </div>
                {renderOverflowTooltip(targetId)}
              </InputGroup>

              {props?.showUrlError !== "" && (
                <div className={cx(style["alert"], style["alert-danger"], style["mt-1"])}>{props.showUrlError}</div>
              )}
            </>
          );
        }
        break;

      default:
        break;
    }

    inputComponent = (
      <FormGroup
        cssModule={style}
        className={cx( style["mb-4"], style["form-group"],
          {
            [style[spinnerClass]]: type === "spinner",
            [style["required-mark"]]: props.required,
            [style[checkBoxClass]]: type === "checkbox",
            [style[customFileClass]]: type === "file",
            [style[multilineClass]]: multilineClass,
          },
          radioClass,
          formGroupClass
        )}>
        {props.label && (
          <Label className={cx(style["align-items-center"], style["d-flex"], style["form-label"])}>
            <div>{props.label}</div>
            {props.showinfo && (
              <div className={style["ps-2"]}>
                <Icon icon="information" id="FieldInfoToolTip" additionalclass="font-14" />
                <UncontrolledTooltip placement="bottom" target="FieldInfoToolTip" innerClassName="mw-100">
                  {props.showinfo}
                </UncontrolledTooltip>
              </div>
            )}
          </Label>
        )}
        {inputElement}
        {type !== "password_length" && props?.showerror !== "" && (
          <div className={cx(style["alert"], style["alert-danger"])}>{props.showerror}</div>
        )}
        {type === "password_length" && (
          <div
            className={cx(
              style["align-items-center"],
              style["d-flex"],
              style["justify-content-between"],
              style["w-100"]
            )}>
            <div
              className={cx(style["alert"], {
                [style["text-danger"]]: props?.showerror !== "",
                [style["text-success"]]: props?.showsuccess !== "",
              })}>
              {props?.showerror !== "" && props.showerror}
              {props?.showsuccess !== "" && (
                <div className={cx(style["align-items-center"], style["d-flex"], style["w-100"])}>
                  <Icon icon="tick-success" additionalclass="me-1"></Icon>
                  {props.showsuccess}
                </div>
              )}
            </div>
            <span>
              {props.length} / {props.limit}
            </span>
          </div>
        )}
      </FormGroup>
    );
  }

  if (returnType && returnType === "inputGroup") {
    switch (type) {
      case "search":
        inputGroupAddon = (
          <div className={cx(style[`input-group-${addonType}`])}>
            <InputGroupText className={style["bg-white"]}>
              <Icon icon="ui-search" />
            </InputGroupText>
          </div>
        );
        inputComponent = (
          <InputGroup id={groupId} cssModule={style} className={style["flex-nowrap"]}>
            {addonType === "prepend" && inputGroupAddon}
            {rInput}
            {addonType === "append" && inputGroupAddon}
          </InputGroup>
        );
        break;

      case "crm-select-deal-input":
        inputGroupAddon = (
           <div className={cx(style[`input-group-${addonType}`])}>
            <InputGroupText className={cx(style["h-100"], style["bg-white"])}>
              <Icon icon="ui-search" />
            </InputGroupText>
          </div>
        );
        inputComponent = (
          <InputGroup id={groupId} cssModule={style} className={style["flex-nowrap"]}>
            {addonType === "prepend" && inputGroupAddon}
            {rInput}
            {addonType === "append" && inputGroupAddon}
          </InputGroup>
        );
        <div>
          {props?.showUrlError !== "" && (
            <div className={cx(style["alert"], style["alert-danger"], style["mt-1"])}>{props.showUrlError}</div>
          )}
        </div>;
        break;

      default:
        break;
    }
  }

  if (returnType && returnType === "noGroup") {
    switch (type) {
      case "text":
        inputComponent = rInput;
        break;

      case "textarea":
        inputComponent = rInput;
        break;

      case "checkbox":
        inputComponent = (
          <React.Fragment>
            {rInput}
            <Label className={cx(style["form-check-label"], style["form-check-label"])} htmlFor={props?.id}>
              {props?.label}
            </Label>
          </React.Fragment>
        );
        break;

      case "contentEditable":
        inputComponent = <ContentEditable {...props} />;
        break;

      case "radio":
        inputComponent = (
          <div
            className={cx(
              style["custom-control"],
              style["custom-radio"],
              style["custom-control-inline"],
              style["form-check"],
              style[`${["portrait", "landscape"].includes(props.value) ? props.value : ""}`]
            )}>
            {rInput}
            <Label className={cx(style["form-check-label"])} htmlFor={props.id}>
              {props.text}
            </Label>
            {props.icon && <Icon icon={props.icon} />}
          </div>
        );
        break;

      default:
        break;
    }
  }

  return inputComponent;
};

Input.propTypes = {
  extn: PropTypes.bool,
  isdOptions: PropTypes.array,
  fields: PropTypes.object,
  updateState: PropTypes.func,
  phoneDefaultValue: PropTypes.object,
  type: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  name: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  toggleHandler: PropTypes.func,
  checkBoxClass: PropTypes.string,
  returnType: PropTypes.string,
  addonType: PropTypes.string,
  groupId: PropTypes.string,
  text: PropTypes.any,
  id: PropTypes.string,
  showerror: PropTypes.string,
  showsuccess: PropTypes.string,
  length: PropTypes.number,
  limit: PropTypes.number,
  spinnerClass: PropTypes.string,
  onIncrease: PropTypes.func,
  onDecrease: PropTypes.func,
  radioClass: PropTypes.string,
  customFileClass: PropTypes.string,
  inputGroupClass: PropTypes.string,
  multilineClass: PropTypes.string,
  decreasedisable: PropTypes.bool,
  increasedisable: PropTypes.bool,
  formGroupClass: PropTypes.string,
};
